/**
 * Shared visual-state utility classes.
 *
 * Single source of truth for "this thing is the active selection"
 * styling. Sidebar entries, selected cards, and any other UI that
 * conveys a "this is the active choice" affordance read from here so
 * the look is uniform across pages.
 *
 * Current pattern: a 2px primary outline + 5% primary tint, no
 * full-saturation fill. The card variant adds shadow-md; the sidebar
 * variant omits shadow and uses `ring` (so the outline doesn't
 * displace inner content) to fit the narrower column. Both surfaces
 * share the same affordance language so customers see one "this is
 * active" treatment across the app.
 *
 * History — #288 originally landed a full-saturation fill (slab)
 * because the pre-#288 thin-1px-border treatment was described as
 * needing "an eagle eye." The current outline+tint+ring is the third
 * iteration: legible like the slab, consistent with the card style,
 * doesn't push content around. The "Test Suites" wrap pitfall the
 * #288 comment warned about is solved by `ring-inset` (no width
 * impact) rather than by avoiding outline-based affordances.
 *
 * When wiring into a component:
 *   import { selectedCardClasses } from '@/lib/styles'
 *   className={cn(base, isActive ? selectedCardClasses : inactiveClasses)}
 *
 * Use `selectedSidebarClasses` for sidebar nav items and
 * `selectedCardClasses` for full-width selectable cards / list rows.
 * Each surface still owns its own *unselected* class set; this module
 * only ships the active half so unselected hover/focus styles stay
 * specific to the surface.
 */

/** Active state for sidebar entries — a soft primary outline + 5%
 * tint, no fill slab. Matches `selectedCardClasses` visually without
 * eating into the cell's content width: `ring` draws outside the
 * border box (vs `border`, which is in the flow), so toggling
 * selection doesn't move the icon/label by 4px and "Test Suites"
 * stays on one line in the 84px sidebar column.
 *
 * Earlier (#288) approach was a full-saturation fill; user feedback
 * was that the slab read as heavy next to lighter card states
 * elsewhere — the outline treatment is consistent without losing
 * legibility. Shadow is intentionally omitted (it doesn't fit the
 * 84px sidebar column the way it does a full-width card). Label
 * weight stays unbolded — bold widened "Test Suites" past the
 * column and forced a wrap. */
export const selectedSidebarClasses = 'ring-2 ring-primary ring-inset bg-primary/5'

/** Selected state for card-style items. Calibrated for full-width
 * cards (Generation/Replay templates on the runner, test-case rows
 * on the plan): strong primary border + a soft 5% tint + slight
 * elevation. The tint is the same opacity the project uses
 * elsewhere for selections (Header dropdown highlight, workspace
 * picker active row), kept low so the fill reads as a soft wash
 * rather than a slab — the 2px primary border + shadow do most of
 * the visual work. The card's existing `font-semibold` title stays
 * the bolder anchor; no extra weight at the outer layer. */
export const selectedCardClasses = 'border-2 border-primary bg-primary/5 shadow-md'
